/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';






import OpmsField from '../../components/OpmsForm/OpmsField';

import OpmsInput from '../../components/OpmsForm/OpmsInput';


import Select from '../../components/OpmsForm/Fields/Select';

import ToggleButtonGroup from '../../components/OpmsForm/Fields/ToggleButtonGroup';

import InputAdornment from '@material-ui/core/InputAdornment';
import PatientBreadcrumbs from '../../components/Breadcrumbs/PatientBreadcrumbs';
import Checkbox from '../../components/OpmsForm/Fields/Checkbox';
import DatePicker from '../../components/OpmsForm/Fields/DatePicker';
import NumericField, { FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE, THREE_DIGIT_POSITIVE_INTEGER } from '../../components/OpmsForm/Fields/NumericField';
import OpmsStaticGrid, { IOpmsStaticGridHeader } from '../../components/OpmsForm/Grids/Static/OpmsStaticGrid';
import OpmsStaticRow, { IOpmsStaticRow } from '../../components/OpmsForm/Grids/Static/OpmsStaticRow';
import OpmsConditional, { IOpmsContionParams } from '../../components/OpmsForm/OpmsConditional';
import OpmsFieldLabel from '../../components/OpmsForm/OpmsFieldLabel';
import TrialForm from '../../components/OpmsForm/TrialForm';
import { GetFieldLookupItem } from '../../context/LookupsContext';
import { Paper, Tooltip } from '@material-ui/core';
import { PatientContext } from '@ngt/online-patient-management-ui';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IGeneralProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(0)
    },
    subHeading: {
        padding: theme.spacing(3)
    },
    test: {
        flex: '1 1 auto'
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const hasMenopausalStatusOfPost = ({ lookups, formState }: IOpmsContionParams<Dtos.General>) => GetFieldLookupItem(lookups, 'MenopausalStatusId', formState.values.menopausalStatusId!)?.type === Dtos.LookupMenopausalStatusType.PostMenopausal;
const hasMenopausalStatusOfPre = ({ lookups, formState }: IOpmsContionParams<Dtos.General>) => GetFieldLookupItem(lookups, 'MenopausalStatusId', formState.values.menopausalStatusId!)?.type === Dtos.LookupMenopausalStatusType.PreOrPeriMenopausal;
const hasPostMenopausalStatusRequiresSpecification = ({ lookups, formState }: IOpmsContionParams<Dtos.General>) => GetFieldLookupItem(lookups, 'PostmenopausalSpecificationId', formState.values.postmenopausalSpecificationId!)?.type === Dtos.LookupSpecificationType.RequiresSpecification;

const doesNotHaveLhLlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.General>) => !formState.values.postmenopausalLevelsLhLlnNotSpecified;
const doesNotHaveLhUlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.General>) => !formState.values.postmenopausalLevelsLhUlnNotSpecified;
const doesNotHaveFshLlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.General>) => !formState.values.postmenopausalLevelsFshLlnNotSpecified;
const doesNotHaveFshUlnNotSpecified = ({ formState }: IOpmsContionParams<Dtos.General>) => !formState.values.postmenopausalLevelsFshUlnNotSpecified;

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const levelsHeader: IOpmsStaticGridHeader[] = [
    {
        columnName: 'test',
        content: 'Test',
        width: '14%',
        minWidth: 100
    },
    {
        columnName: 'value',
        content: '',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'lln',
        content: 'Post Menopausal LLN',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'llnNotSpecified',
        content: '',
        width: '10%',
        minWidth: 100
    },
    {
        columnName: 'uln',
        content: 'Post Menopausal ULN',
        width: '22%',
        minWidth: 150
    },
    {
        columnName: 'ulnNotSpecified',
        content: '',
        width: '10%',
        minWidth: 100
    }
];

const lhRow: Array<IOpmsStaticRow<Dtos.General>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'PostmenopausalLevelsLh'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'PostmenopausalLevelsLh'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveLhLlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsLhLln'
    },
    {
        columnName: 'llnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsLhLlnNotSpecified'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveLhUlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsLhUln'
    },
    {
        columnName: 'ulnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsLhUlnNotSpecified'
    }
];

const fshRow: Array<IOpmsStaticRow<Dtos.General>> = [
    {
        columnName: 'test',
        content: (
            <OpmsFieldLabel />
        ),
        fieldName: 'PostmenopausalLevelsFsh'
    },
    {
        columnName: 'value',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        fieldName: 'PostmenopausalLevelsFsh'
    },
    {
        columnName: 'lln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveFshLlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsFshLln'
    },
    {
        columnName: 'llnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsFshLlnNotSpecified'
    },
    {
        columnName: 'uln',
        content: (
            <OpmsInput
                component={NumericField}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            U/L&nbsp;
                        </InputAdornment>
                    )
                }}
                numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
            />
        ),
        condition: doesNotHaveFshUlnNotSpecified,
        subscription: { values: true },
        fieldName: 'PostmenopausalLevelsFshUln'
    },
    {
        columnName: 'ulnNotSpecified',
        content: (
            <OpmsInput
                component={Checkbox}
                label={'NA'}
            />
        ),
        fieldName: 'PostmenopausalLevelsFshUlnNotSpecified'
    }
];

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const General: React.FunctionComponent<IGeneralProps> = () => {
    const classes = useStyles();

    const [patient] = React.useContext(PatientContext);

    const patientCohortC = (patient as Dtos.Patient)?.treatmentId !== 1 && (patient as Dtos.Patient)?.treatmentId !== 2;

    return (
        <PageLayout
            breadcrumbs={
                <PatientBreadcrumbs />
            }
            heading="General"
        >
            <Paper
                className={classes.container}
            >
                <TrialForm
                    formClass={Dtos.General}
                >
                    <OpmsField name="Age">
                        <OpmsInput
                            component={NumericField}
                            numberFormat={THREE_DIGIT_POSITIVE_INTEGER}
                        />
                    </OpmsField>
                    <OpmsField name="PhysicalExaminationDate">
                        <OpmsInput
                            component={DatePicker}
                        />
                    </OpmsField>
                    <OpmsField name="Height">
                        <OpmsInput
                            component={NumericField}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        cm&nbsp;
                                    </InputAdornment>
                                )
                            }}
                            numberFormat={THREE_DIGIT_POSITIVE_INTEGER}
                        />
                    </OpmsField>
                    <OpmsField name="Weight">
                        <OpmsInput
                            component={NumericField}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        kg&nbsp;
                                    </InputAdornment>
                                )
                            }}
                            numberFormat={THREE_DIGIT_POSITIVE_INTEGER}
                        />
                    </OpmsField>
                    <OpmsField name="EcogPerformanceStatusId">
                        <OpmsInput
                            component={Select}
                        />
                    </OpmsField>
                    {
                        !patientCohortC && <OpmsField name="MenopausalStatusId" xl={7} lg={7} md={9}>
                            <OpmsInput
                                component={Select}
                            />
                        </OpmsField>
                    }

                    {
                        !patientCohortC && <OpmsConditional
                            formClass={Dtos.General}
                            mode="Show"
                            condition={hasMenopausalStatusOfPost}
                            subscription={{ values: true }}
                        >
                            <OpmsField name="PostmenopausalSpecificationId" xl={9} lg={9} md={8}>
                                <OpmsInput
                                    component={Select}
                                />
                            </OpmsField>
                            <OpmsConditional
                                formClass={Dtos.General}
                                mode="Show"
                                condition={hasPostMenopausalStatusRequiresSpecification}
                                subscription={{ values: true }}
                            >
                                <OpmsField name="PostmenopausalLevelsDate">
                                    <OpmsInput
                                        component={DatePicker}
                                    />
                                </OpmsField>
                                <OpmsStaticGrid
                                    headers={levelsHeader}
                                >
                                    <OpmsStaticRow
                                        cells={lhRow}
                                    />
                                    <OpmsStaticRow
                                        cells={fshRow}
                                    />
                                </OpmsStaticGrid>
                            </OpmsConditional>
                        </OpmsConditional>
                    }

                    {
                        !patientCohortC && <OpmsConditional
                            formClass={Dtos.General}
                            mode="Show"
                            condition={hasMenopausalStatusOfPre}
                            subscription={{ values: true }}
                        >
                            <OpmsField name="PreOrPerimenopausalNegativePregnancyTestId">
                                <OpmsInput
                                    component={ToggleButtonGroup}
                                />
                            </OpmsField>
                            <OpmsField name="PreOrPerimenopausalNegativePregnancyTestDate">
                                <OpmsInput
                                    component={DatePicker}
                                />
                            </OpmsField>
                        </OpmsConditional>
                    }
                    
                    <OpmsField name="LvefId">
                        <OpmsInput
                            component={ToggleButtonGroup}
                        />
                    </OpmsField>
                    <OpmsField name="LvefDate">
                        <OpmsInput
                            component={DatePicker}
                        />
                    </OpmsField>
                    <OpmsField
                        name="LvefValue"
                        instruction={
                            patientCohortC ? 
                                <small>
                                    <br />
                                    <i>Note: If range is reported for LVEF, please record the lowest value.</i>
                                </small>
                                :
                            undefined
                        }
                    >
                        <OpmsInput
                            component={NumericField}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        %&nbsp;
                                    </InputAdornment>
                                )
                            }}
                            numberFormat={FIVE_DIGIT_INTEGER_TWO_DECIMAL_PLACES_POSITIVE}
                        />
                    </OpmsField>
                    <OpmsField name="EcgDate">
                        <OpmsInput
                            component={DatePicker}
                        />
                    </OpmsField>
                    <OpmsField name="PlannedStartDate">
                        <OpmsInput
                            component={DatePicker}
                        />
                    </OpmsField>
                </TrialForm>
            </Paper>
        </PageLayout>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default General;